import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LabelDisplayedRowsArgs,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField
} from '@mui/material';
import {
  AddCircle,
  AssignmentReturned,
  Close,
  ExpandLess,
  ExpandMore,
  RemoveCircleRounded,
  Search
} from '@mui/icons-material';
import { Theme } from '@mui/system';
import { buttons, Colors, shapes } from '../../../utils/theme';
import { displayText } from '../../../utils/ui';
import Loader from '../Loader';
import details from '../../../assets/images/actions/details.svg';
import remove from '../../../assets/images/actions/trash.svg';
import remove_gray from '../../../assets/images/actions/trash_gray.svg';
import { Roles } from '../../../enums/RoleEnums';
import { Types } from '../../../enums/TypeEnums';
import { canUserDelete } from '../../../utils/rightUtils';
import { AppContext } from '../../../AppContext';
import { INVOICE_STATUS_CONFIRMED } from '../../../enums/AppConsts';
import { getCellValue, getComparator, IHeadCell } from './OverviewTable';

type Order = 'asc' | 'desc';

type Data = any;

interface IProps {
  columns: IHeadCell[];
  data: Data[];
  hidePagination?: boolean;
  rowsPerPageOptions?: number[];
  pageSize?: number;
  createButtonProps?: CreateButtonProps;
  exportButtonProps?: IActionButtonProps;
  showSearch?: boolean;
  handleDetails?: (rowData: Data) => void;
  handleRemove?: (rowData: Data) => void;
  additionalFilters?: JSX.Element;
  loading?: boolean;
  crudType?: string;
  removeButtonStyle?: 'basket' | 'minus';
  title?: string;
  detailPanel?: (rowData: Data) => React.ReactNode;
  toolbarBackground?: string;
  maxHeight?: number;
  minBody?: number;
  additionalToolbar?: boolean;
  fixedHeaders?: boolean;
}

interface IActionButtonProps {
  id: string;
  onClick: () => void;
}

interface ITableHeadersProps {
  columns: IHeadCell[];
  order: Order;
  orderBy: IHeadCell | null;
  onRequestSort: (property: IHeadCell) => void;
  extraCol: boolean;
  fixedHeaders: boolean;
}

interface CreateButtonProps {
  id: string;
  onClick: () => void;
  text: string;
  disabled?: boolean;
}

const getTableRowCellStyle = (index: number, width: number | undefined) =>
  ({
    border: 'none',
    left: 0,
    textAlign: 'center',
    color: 'primary.main',
    padding: 0,
    width: width ? `${width}px` : 'auto',
    zIndex: index === 0 ? 2 : 1
  } as SxProps);

const tableRowStyle: SxProps = {
  padding: 0,
  height: '40px',
  color: Colors.main,
  textAlign: 'center',
  opacity: 1,
  border: 'none'
};

const tableHeaderCellFontStyle: SxProps = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '15px',
  zIndex: 1,
  textAlign: 'center',
  color: Colors.main,
  border: 'none'
};

const tableHeaderCellStyle: SxProps<Theme> = {
  ...tableHeaderCellFontStyle,
  borderRight: '1px solid #FFFFFF'
};

const actionButtonIconStyle: SxProps = { marginRight: '7px' };

const TableHeaders = ({
  columns,
  order,
  orderBy,
  onRequestSort,
  extraCol,
  fixedHeaders
}: ITableHeadersProps) => (
  <TableHead>
    <TableRow
      sx={{
        background: '#B4D4F9',
        height: '22px',
        ...(fixedHeaders && { position: 'sticky', top: 0, zIndex: 2 })
      }}
    >
      {extraCol && (
        <TableCell
          padding="none"
          sx={{
            ...tableHeaderCellStyle,
            width: '60px'
          }}
        ></TableCell>
      )}
      {columns.map((col, index) => (
        <TableCell
          key={index}
          align={col.type === 'numeric' ? 'right' : 'left'}
          padding="none"
          sortDirection={orderBy === col ? order : false}
          sx={{
            ...tableHeaderCellStyle,
            padding: col.sorting !== false ? 0 : '0 13px',
            width: col.width ? `${col.width}px` : 'auto'
          }}
        >
          {col.sorting !== false ? (
            <TableSortLabel
              active={orderBy === col}
              direction={orderBy === col ? order : 'asc'}
              onClick={() => onRequestSort(col)}
              sx={{
                marginLeft: col.type === 'numeric' ? 0 : '26px',
                marginRight: col.type !== 'numeric' ? 0 : '26px'
              }}
            >
              <Box sx={{ ...tableHeaderCellFontStyle }}>{col.title}</Box>
            </TableSortLabel>
          ) : (
            col.title
          )}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const DenseTable: React.FunctionComponent<IProps> = ({
  columns,
  data,
  hidePagination,
  rowsPerPageOptions,
  pageSize,
  createButtonProps,
  exportButtonProps,
  showSearch,
  handleDetails,
  handleRemove,
  loading = false,
  crudType,
  additionalFilters,
  additionalToolbar,
  removeButtonStyle = 'basket',
  detailPanel,
  fixedHeaders = false
}: IProps) => {
  const { state } = useContext(AppContext);
  const deletableItems = !data || data.length === 0 ? false : canUserDelete(state.user, crudType);
  const itemsPerPageOptions = rowsPerPageOptions ?? [10, 15, 25, 50];
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<IHeadCell | null>(null);
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(
    pageSize ?? itemsPerPageOptions.length > 1 ? itemsPerPageOptions[1] : itemsPerPageOptions[0]
  );
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);

  useEffect(() => {
    setFilteredData(data);
    filterData(searchValue);
    setPage(0);
  }, [data]);

  const handleRequestSort = (property: IHeadCell) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    const newOrderBy = isAsc ? property : orderBy !== property ? property : null;
    setOrderBy(newOrderBy);
  };

  const getReadyToUseData = () => {
    let rows = filteredData.slice();
    if (orderBy !== null) {
      rows = rows.sort(getComparator(order, orderBy));
    }
    if (hidePagination !== true) {
      rows = rows.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage);
    }
    return rows;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchValue(value);
    filterData(value);
    setPage(0);
  };

  const filterData = (filter: string) => {
    const trimmedSearchText = filter.trim();
    setFilteredData(
      data.filter((row) => {
        return columns
          .filter((columnDef) => {
            return columnDef.searchable === undefined ? true : columnDef.searchable;
          })
          .some((columnDef) => {
            if (columnDef.customFilterAndSearch) {
              return !!columnDef.customFilterAndSearch(trimmedSearchText, row, columnDef);
            } else if (columnDef.field) {
              const value = getCellValue(row, columnDef.field);
              return value?.toString().toUpperCase().includes(trimmedSearchText.toUpperCase());
            }
          });
      })
    );
  };

  const generateToolbar = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'end',
          marginBottom: '14px',
          padding: '0 24px',
          gap: '20px'
        }}
      >
        <Box sx={{ flexGrow: 0 }}>
          {showSearch === true && (
            <TextField
              id="table-search-field"
              value={searchValue}
              onChange={handleValueChange}
              sx={{
                color: Colors.main,
                backgroundColor: Colors.input_field_background,
                width: '365px',
                height: '36px',
                borderRadius: '9px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  }
                }
              }}
              placeholder={'Search...'}
              InputProps={{
                startAdornment: (
                  <Search
                    sx={{
                      color: Colors.main,
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                      input: { '&::placeholder': { color: Colors.main } }
                    }}
                  />
                ),
                endAdornment: (
                  <IconButton
                    disabled={!Boolean(searchValue)}
                    onClick={() => {
                      setSearchValue('');
                      filterData('');
                      setPage(0);
                    }}
                  >
                    <Close
                      sx={{
                        color: searchValue ? Colors.main : '#9CA3AE',
                        width: '20px',
                        height: '20px'
                      }}
                    />
                  </IconButton>
                ),
                style: {
                  height: '36px',
                  color: Colors.main,
                  paddingRight: '4px'
                }
              }}
              className={'custom-search'}
            />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>{additionalFilters}</Box>
        <Box sx={{ flexGrow: 0, display: 'flex', gap: '10px' }}>
          {exportButtonProps && (
            <Button
              id={exportButtonProps.id}
              data-testid={exportButtonProps.id}
              onClick={() => exportButtonProps.onClick()}
              sx={buttons.cancel}
            >
              <AssignmentReturned sx={actionButtonIconStyle} />
              Export
            </Button>
          )}
          {createButtonProps && (
            <Button
              id={createButtonProps.id}
              onClick={() => createButtonProps.onClick()}
              disabled={createButtonProps.disabled || false}
              variant="contained"
              color="primary"
              sx={buttons.overviewCreate}
            >
              <AddCircle sx={actionButtonIconStyle} />
              {createButtonProps.text}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const shouldShowToolbar =
    showSearch === true || createButtonProps || exportButtonProps || additionalFilters;

  const detailsButton = (rowData: Data) => (
    <Button
      id={
        rowData.name
          ? rowData.name
          : rowData.number
          ? rowData.number
          : `${rowData.email}-${rowData.company?.name}`
      }
      data-testid={rowData.id}
      sx={{ width: 'auto' }}
      onClick={() =>
        handleDetails
          ? handleDetails(rowData)
          : console.log('Missing handle details function', rowData)
      }
    >
      <img src={details} alt="detailsButton" />
    </Button>
  );

  const removeButton = (rowData: Data) => {
    const disableDelete = shouldDisableDelete(rowData);
    return (
      <Button
        id={rowData.name ? `${rowData.name}-delete` : `${rowData.email}-delete`}
        data-testid={`${rowData.id}-delete`}
        sx={{ width: 'auto' }}
        disabled={disableDelete}
        onClick={() =>
          handleRemove
            ? handleRemove(rowData)
            : console.log('Missing handle remove function', rowData)
        }
      >
        {disableDelete ? (
          removeButtonStyle === 'basket' ? (
            <img src={remove_gray} alt="removeButtonGray" />
          ) : (
            <RemoveCircleRounded sx={{ color: Colors.gray, width: 20, height: 21 }} />
          )
        ) : removeButtonStyle === 'basket' ? (
          <img src={remove} alt="removeButton" />
        ) : (
          <RemoveCircleRounded sx={{ color: Colors.skyBlue, width: 20, height: 21 }} />
        )}
      </Button>
    );
  };

  const shouldDisableDelete = (rowData: Data) => {
    if (rowData.__typename === Types.Invoice && rowData.status.name === INVOICE_STATUS_CONFIRMED) {
      return true;
    } else if (
      rowData.__typename === Types.Nav ||
      rowData.__typename === Types.DividendGross ||
      rowData.__typename === Types.HistoryLtv ||
      rowData.__typename === Types.HistoryKpi ||
      rowData.__typename === Types.HistoryAward ||
      rowData.__typename === Types.AllocationSector ||
      rowData.__typename === Types.HistoryAllocationCountry ||
      rowData.__typename === Types.HistoryRecalc
    ) {
      const restrictedRoles = [Roles.Admin, Roles.IndexOperator];
      return !restrictedRoles.some((r) => state.user.data?.roles.includes(r));
    } else {
      return !deletableItems;
    }
  };

  const styledColumns = columns.map((column) => {
    let res = {
      ...column
    };
    if (column.title === 'Details') {
      res = {
        ...res,
        render: detailsButton
      };
    } else if (column.title === 'Remove') {
      res = {
        ...res,
        render: removeButton
      };
    }
    return res;
  });

  const shouldShowPanel = Boolean(detailPanel);

  return (
    <Paper
      sx={{
        width: '100%',
        height: additionalToolbar ? 'calc(100% - 50px)' : '100%',
        padding: 0,
        boxShadow: '0px 10px 15px rgba(8, 79, 180, 0.05)',
        borderRadius: '8px'
      }}
    >
      {shouldShowToolbar && generateToolbar()}
      <TableContainer sx={{ height: hidePagination !== true ? 'calc(100% - 52px)' : '100%' }}>
        <Table aria-labelledby="tableTitle">
          <TableHeaders
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            columns={columns}
            extraCol={shouldShowPanel}
            fixedHeaders={fixedHeaders}
          />
          <TableBody>
            {getReadyToUseData().map((rowData, index) => (
              <React.Fragment key={index}>
                <TableRow
                  // hover
                  onClick={
                    detailPanel
                      ? () => setSelectedRowId(rowData.id === selectedRowId ? null : rowData.id)
                      : undefined
                  }
                  sx={{
                    ...tableRowStyle,
                    cursor: handleDetails ? 'pointer' : 'default',
                    background: index % 2 === 0 ? '#DFDFE033' : 'white'
                  }}
                >
                  {shouldShowPanel && (
                    <TableCell
                      sx={{
                        border: 'none',
                        padding: 0,
                        textAlign: 'center'
                      }}
                    >
                      <IconButton>
                        {selectedRowId !== rowData.id ? (
                          <ExpandMore style={{ color: Colors.skyBlue }} />
                        ) : (
                          <ExpandLess style={{ color: Colors.skyBlue }} />
                        )}
                      </IconButton>
                    </TableCell>
                  )}
                  {styledColumns.map((col, index2) => (
                    <TableCell key={index2} sx={getTableRowCellStyle(index2, col.width)}>
                      {col.render
                        ? col.render(rowData)
                        : displayText(getCellValue(rowData, col.field ?? ''), col.type)}
                    </TableCell>
                  ))}
                </TableRow>
                {selectedRowId === rowData.id && detailPanel && (
                  <TableRow>
                    <TableCell colspan={(columns.length + 1).toString()}>
                      {detailPanel(rowData)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hidePagination !== true && (
        <TablePagination
          rowsPerPageOptions={itemsPerPageOptions}
          showFirstButton={true}
          showLastButton={true}
          component="div"
          count={filteredData.length}
          rowsPerPage={itemsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          backIconButtonProps={{
            contextMenu: 'back',
            color: 'info',
            title: 'Previous'
          }}
          labelDisplayedRows={({ count }: LabelDisplayedRowsArgs) => {
            const pageCount = count > itemsPerPage ? Math.floor(count / itemsPerPage + 1) : 1;
            return `${page + 1} of ${pageCount}`;
          }}
          nextIconButtonProps={{
            color: 'info',
            title: 'Next'
          }}
          labelRowsPerPage={
            <span style={{ fontSize: '14px', lineHeight: '17px', color: Colors.gray }}>
              Total:{' '}
              <span style={{ color: Colors.skyBlue, fontSize: '12px', fontWeight: 700 }}>
                {data.length}
              </span>
            </span>
          }
          SelectProps={{
            renderValue: (props) => `${props} Rows/Page`,
            sx: {
              color: Colors.main,
              border: `1px solid ${Colors.main}`,
              borderRadius: `${shapes.borderRadius}px`,
              backgroundColor: Colors.white,
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 700,
              marginRight: '20px',
              '&.Mui-focused div': {
                borderRadius: `${shapes.borderRadius}px`,
                backgroundColor: Colors.white
              }
            }
          }}
        />
      )}
      <Loader loading={loading} />
    </Paper>
  );
};

export default DenseTable;
